import React, { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { adminApi } from '../lib/adminApi';
import { Button } from '../components/ui/button';
import { ShieldCheck, LayoutDashboard, LogOut, FileText, ChevronDown, Users } from 'lucide-react';
import './Admin.css';
import { blogNavigation } from './admin/BlogNavTabs';

const message = error => typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Could not complete the request. Please retry.';

import PageSEO from '../components/PageSEO';

export function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const { data } = await adminApi.post('/admin/login', { email, password });
      sessionStorage.setItem('admin_token', data.token);
      setPassword(''); navigate('/admin', { replace: true });
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  }
  return <main className="admin-ui admin-login">
    <PageSEO title="Admin Login | AI Search Advisers" isPrivate={true} />
    <form onSubmit={submit} className="admin-login-card space-y-5">
      <div className="admin-brand"><span className="admin-brand-icon"><ShieldCheck size={22} aria-hidden="true" /></span><span>AI Search Advisers<small>Administration</small></span></div><div><h3 className="admin-title">Admin Login</h3><p className="admin-description">Sign in to manage accounts and verification requests.</p></div>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <label className="block">Email<input className="block w-full border rounded-lg p-3 mt-2" type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} /></label>
      <label className="block">Password<input className="block w-full border rounded-lg p-3 mt-2" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} /></label>
      <Button className="admin-primary w-full" disabled={busy}>{busy ? 'Signing in…' : 'Sign In'}</Button>
    </form>
  </main>;
}

export function AdminContentLoader({ error, retry }) {
  if (error) {
    return (
      <div className="admin-loader-fade admin-loader-wrapper">
        <p role="alert">{error}</p>
        <Button onClick={retry}>Retry</Button>
      </div>
    );
  }
  return (
    <div className="admin-loader-fade admin-loader-wrapper">
      <div className="admin-spinner"></div>
      <p>Loading...</p>
    </div>
  );
}

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkedPath, setCheckedPath] = useState(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setError('');
    adminApi.get('/admin/me').then(() => { if (active) setCheckedPath(location.pathname); })
      .catch(e => { if (active) { if (e.response?.status === 401) setDenied(true); else setError(message(e)); } });
    return () => { active = false; };
  }, [location.pathname, retry]);
  useEffect(() => {
    const expired = () => setDenied(true);
    window.addEventListener('admin-session-expired', expired);
    return () => window.removeEventListener('admin-session-expired', expired);
  }, []);
  if (denied) return <Navigate to="/admin-login" replace />;
  
  async function logout() {
    setBusy(true); setError('');
    try {
      await adminApi.post('/admin/logout');
      sessionStorage.removeItem('admin_token'); navigate('/admin-login', { replace: true });
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  }
  
  const isLoadingSession = checkedPath !== location.pathname;

  return <div className="admin-ui admin-shell">
    <PageSEO title="Admin | AI Search Advisers" isPrivate={true} />
    <aside className="admin-sidebar">
      <div className="admin-brand"><span className="admin-brand-icon"><ShieldCheck size={22} aria-hidden="true" /></span><span>AI Search Advisers<small>Administration</small></span></div>
      <nav aria-label="Admin navigation" className="admin-nav">
        <NavLink to="/admin" end className={({ isActive }) => `admin-nav-link ${isActive ? 'is-active' : ''}`}><LayoutDashboard size={19} /><span>Dashboard</span></NavLink>
        <details className="admin-blog-group" open={blogNavigation.some(({ to }) => location.pathname === to || location.pathname.startsWith(to + '/'))}>
          <summary className="admin-nav-link"><FileText size={19} /><span>Blog Posts</span><ChevronDown size={16} className="admin-nav-chevron" /></summary>
          <div className="admin-blog-subnav">
            {blogNavigation.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} className={({ isActive }) => `admin-nav-link ${isActive ? 'is-active' : ''}`}><Icon size={16} aria-hidden="true" /><span>{label}</span></NavLink>)}
          </div>
        </details>
        <NavLink to="/admin/users" className={({ isActive }) => `admin-nav-link ${isActive ? 'is-active' : ''}`}><Users size={19} /><span>Users</span></NavLink>
        <NavLink to="/admin/veteran-verifications" className={({ isActive }) => `admin-nav-link ${isActive ? 'is-active' : ''}`}><ShieldCheck size={19} /><span>Veteran Verifications</span></NavLink>
      </nav>
      <Button onClick={logout} disabled={busy} className="admin-logout"><LogOut size={17} aria-hidden="true" />Logout</Button>
      {error && !isLoadingSession && <p role="alert">{error}</p>}
    </aside>
    <div className="admin-content">
      {isLoadingSession ? (
        <AdminContentLoader error={error} retry={() => setRetry(retry + 1)} />
      ) : (
        <Outlet />
      )}
    </div>
  </div>;
}

export function AdminOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => { let active = true; setError(''); adminApi.get('/admin/overview').then(r => { if (active) setData(r.data); }).catch(e => { if (active) setError(message(e)); }); return () => { active = false; }; }, [retry]);
  const labels = { total_users: 'Total Users', active_users: 'Active Users', paused_users: 'Paused Users', active_subscriptions: 'Active Subscriptions', pending_veteran_verifications: 'Pending Veteran Verifications', total_blogs: 'Total Blog Posts' };
  return <main className="space-y-6"><div className="admin-page-heading"><p className="admin-eyebrow">Overview</p><h3 className="admin-title">Dashboard</h3><p className="admin-description">Account activity and verification requests at a glance.</p></div>
    {error && <div role="alert">{error} <Button onClick={() => setRetry(retry + 1)}>Retry</Button></div>}
    {!data && !error && <p role="status">Loading overview…</p>}
    {data && <><div className="admin-metrics">{Object.entries(labels).map(([key, label]) => <div key={key} className="admin-metric"><p className="text-sm text-slate-600">{label}</p><p className="admin-metric-value">{data[key] ?? 'Unavailable'}</p></div>)}</div>{data.active_subscriptions === null && <p>Stripe subscription data is currently unavailable. Please check billing configuration and retry.</p>}</>}
  </main>;
}

export function AdminUsers() {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true; setLoading(true); setError('');
    const timer = setTimeout(() => adminApi.get('/admin/users', { params: { search, offset } }).then(r => { if (active) setData(r.data); }).catch(e => { if (active) { setError(message(e)); setData(null); } }).finally(() => { if (active) setLoading(false); }), 250);
    return () => { active = false; clearTimeout(timer); };
  }, [search, offset, retry]);
  async function toggle(user) {
    setBusy(user.id); setError('');
    try {
      const { data: result } = await adminApi.put(`/admin/users/${encodeURIComponent(user.id)}/status`, { account_status: user.account_status === 'paused' ? 'active' : 'paused' });
      setData(previous => ({ ...previous, items: previous.items.map(item => item.id === user.id ? { ...item, account_status: result.account_status } : item) }));
    } catch (e) { setError(message(e)); } finally { setBusy(null); }
  }
  return <main className="space-y-6">
    <div className="admin-page-heading mb-6">
      <p className="admin-eyebrow">User Management</p>
      <h3 className="admin-title">Users</h3>
      <p className="admin-description">Manage application access. Pausing an account does not change Stripe billing.</p>
    </div>
    <label className="admin-search block max-w-md">Search by name or email<input type="search" className="mt-2 block border rounded-lg p-3 w-full" value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }} /></label>
    {error && <p role="alert" className="text-red-700">{error} <Button onClick={() => setRetry(retry + 1)}>Retry</Button></p>}
    {loading && <p role="status">Loading users…</p>}
    {data && !data.billing_available && <p role="status">Stripe is unavailable. Stored plan names are shown; subscription statuses cannot be verified.</p>}
    {data && !loading && <><div className="admin-table-wrap"><table className="admin-users-table"><thead><tr>{['Name', 'Email', 'Tenant ID', 'Account Status', 'Subscription Plan', 'Subscription Status', 'Created Date', 'Action'].map(label => <th key={label} className="text-left p-3 border-b whitespace-nowrap">{label}</th>)}</tr></thead><tbody>{data.items.map(user => <tr key={user.id} className="border-b"><td data-label="Name" className="p-3">{user.name || '—'}</td><td data-label="Email" className="p-3">{user.email}</td><td data-label="Tenant ID" className="p-3 text-xs">{user.id}</td><td data-label="Account Status" className="p-3 capitalize"><span className={`admin-status ${user.account_status === 'paused' ? 'is-paused' : 'is-active'}`}>{user.account_status}</span></td><td data-label="Subscription Plan" className="p-3">{user.subscription_plan || 'Not recorded'}</td><td data-label="Subscription Status" className="p-3">{user.subscription_status || (data.billing_available ? 'No linked subscription' : 'Unavailable')}</td><td data-label="Created Date" className="p-3">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td><td data-label="Action" className="p-3"><Button variant="outline" disabled={!!busy} onClick={() => toggle(user)}>{busy === user.id ? 'Saving…' : user.account_status === 'paused' ? 'Activate User' : 'Pause User'}</Button></td></tr>)}</tbody></table>{!data.items.length && <p className="p-6">No users found.</p>}</div><div className="flex gap-3"><Button variant="outline" disabled={!offset || !!busy} onClick={() => setOffset(offset - 50)}>Previous</Button><Button variant="outline" disabled={!data.has_more || !!busy} onClick={() => setOffset(offset + 50)}>Next</Button></div></>}
  </main>;
}
